import React from 'react';
import { useTranslation } from 'react-i18next';
import Money from '../../Common/Money/Money';
import { useSelector } from 'react-redux';
import styles from './summary.module.css';
import { formatNumberDecimals } from '../../../helpers/format';

const Summary = ({ reservation }) => {
  const { t } = useTranslation();
  const currency = useSelector((state) => state.currency.selected);
  const transportation = useSelector((state) => state.reservation.transportation);

  const renderProductByAgeRange = () => {
    const hasAgeRanges =
      reservation.ageRangesCounts.filter((item) => item.ageRange !== 'GENERIC').length > 0;

    if (hasAgeRanges) {
      return reservation.ageRangesCounts.map((item) => (
        <div className='row mt-2'>
          <div className='col pr-0'>{t('ageRange.numberOf' + item.ageRange)}:</div>
          <div className='col-auto'>{t('ageRange.' + item.ageRange, { count: item.quantity })}</div>
        </div>
      ));
    } else {
      return (
        <div className='row mt-2'>
          <div className='col pr-0'>{t('ageRange.numberOfPeople')}:</div>
          <div className='col-auto'>{t('person', { count: reservation.paxs })}</div>
        </div>
      );
    }
  };

  return (
    <div>
      <div className='font-tiny pb-1 fw-600'>{t('summary')}</div>
      <div className='font-tiny fw-400 mt-2'>
        <div className='text-lighter-gray'>
          {renderProductByAgeRange()}

          <div className='row mt-2'>
            <div className='col pr-0'>{t('productsInfo.priceDetailsFor')}:</div>
            <div className='col-auto'>
              {reservation.items} {reservation.items > 1 ? t('items') : t('item')}
            </div>
          </div>

          <div className='row mt-2 fw-500'>
            <div className='col pr-0'>Sub total:</div>
            <div className='col-auto'>
              <Money value={reservation?.subTotalAmount} symbolSize='14px' />
            </div>
          </div>

          {reservation?.totalDiscount < 0 && (
            <div className='row text-success fw-600 mt-2'>
              <div className='col'>
                {t('discount')}
                {reservation.commission?.discountPercentage !== null &&
                  ` (${reservation?.commission?.discountPercentage}%)`}
                :
              </div>
              <div className='col-auto'>
                <Money value={reservation.totalDiscount} />
              </div>
            </div>
          )}

          {reservation.additionalChargesDetail &&
            reservation.additionalChargesDetail.length > 0 && (
              <>
                {reservation.additionalChargesDetail
                  .filter((charge) => charge.applyOn === 'SUBTOTAL')
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((charge, index) => (
                    <div key={`subtotal-${index}`} className='row mt-2 '>
                      <div className='col pr-0'>
                        {charge.name}{' '}
                        <span className='font-smaller fw-300'>({charge.chargeTarget}%)</span>:
                      </div>
                      <div className='col-auto'>
                        <Money value={charge.chargeAmount} symbolSize='14px' />
                      </div>
                    </div>
                  ))}
              </>
            )}

          {reservation.tipAmount && reservation.tipAmount > 0 ? (
            <div className='row mt-2'>
              <div className='col pr-0'>{t('productsInfo.tip')}:</div>
              <div className='col-auto'>
                <Money value={reservation?.tipAmount} symbolSize='14px' />
              </div>
            </div>
          ) : null}
          {reservation?.pickup && reservation?.pickup.price > 0 && (
            <div className='row mt-2'>
              <div className='col pr-0'> {`${t('transportation')} (${transportation.type})`}:</div>
              <div className='col-auto'>
                <Money value={reservation?.pickup?.price} symbolSize='14px' />
              </div>
            </div>
          )}
          {reservation.taxes && reservation.taxes > 0 ? (
            <div className='row mt-2'>
              <div className='col pr-0'>{t('productsInfo.taxes')}:</div>
              <div className='col-auto'>
                <Money value={reservation?.taxes} symbolSize='14px' />
              </div>
            </div>
          ) : null}
        </div>

        {reservation.additionalChargesDetail && reservation.additionalChargesDetail.length > 0 && (
          <>
            {reservation.additionalChargesDetail
              .filter((charge) => charge.applyOn === 'TOTAL')
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((charge, index) => (
                <div key={`total-${index}`} className='row mt-2'>
                  <div className='col pr-0'>
                    {charge.name}{' '}
                    <span className='font-smaller fw-300'>({charge.chargeTarget}%)</span>:
                  </div>
                  <div className='col-auto'>
                    <Money value={charge.chargeAmount} symbolSize='14px' />
                  </div>
                </div>
              ))}
          </>
        )}

        {reservation.additionalChargesDetail && reservation.additionalChargesDetail.length > 0 && (
          <>
            {reservation.additionalChargesDetail
              .filter((charge) => charge.applyOn === null)
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((charge, index) => (
                <div key={`null-${index}`} className='row mt-2'>
                  <div className='col pr-0'>
                    {charge.name}{' '}
                    <span className='font-smaller fw-300'>({charge.chargeTarget}%)</span>:
                  </div>
                  <div className='col-auto'>
                    <Money value={charge.chargeAmount} symbolSize='14px' />
                  </div>
                </div>
              ))}
          </>
        )}
        <div className='row fw-700 font-1rem mt-2 pt-1'>
          <div className='col pr-0'>Total ({currency.shortForm}):</div>
          <div className='col-auto'>
            <Money value={reservation?.totalAmount} symbolSize='17.6px' />
          </div>
        </div>

        {reservation.remainingAmount > 0 ? (
          <>
            <div className='row mt-2'>
              <div className='col bold pr-0'>{t('productsInfo.depositGuarantee')}:</div>
              <div className='col-auto bold'>
                <Money value={reservation?.depositAmount} symbolSize='14px' />
              </div>
            </div>
            <div className='row mt-2'>
              <div className='col bold pr-0'>{t('productsInfo.balanceToBePaid')}:</div>
              <div className='col-auto bold'>
                <Money value={reservation?.remainingAmount} symbolSize='14px' />
              </div>
            </div>
          </>
        ) : null}
        {reservation.commission && (
          <div className={`${styles.earnings} font-tiny mt-3`}>
            <div className='col-12'>
              {reservation.commission.discountAmount < 0 && (
                <>
                  <div className='row'>
                    <div className='col'>
                      {t('initialCommission')} ({reservation.commission.percentage}%):
                    </div>
                    <div className='col-auto'>
                      <Money value={reservation.commission.subtotal} />
                    </div>
                  </div>

                  <div className='row my-2'>
                    <div className='col'>
                      {t('discountApplied')} ({reservation.commission.discountPercentage}%):
                    </div>
                    <div className='col-auto'>
                      <Money value={reservation.commission.discountAmount} />
                    </div>
                  </div>
                </>
              )}

              <div className='row fw-700'>
                <div className='col'>
                  {t('finalCommission')} (
                  {formatNumberDecimals(
                    reservation.commission.percentage - reservation.commission.discountPercentage,
                  )}
                  %):
                </div>
                <div className='col-auto'>
                  <Money value={reservation.commission.total} />
                </div>
              </div>
              {reservation.commission.discountAmount < 0 && (
                <div className='font-extra-small text-center text-alternative mt-2'>
                  {t('commissionAdjusted', {
                    initialCommission: reservation.commission.percentage,
                    finalCommission: formatNumberDecimals(
                      reservation.commission.percentage - reservation.commission.discountPercentage,
                    ),
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* <div className={`${styles.priceText} text-center font-tiny my-3`}>
          {t('priceIn', { currency: currency.name })}
        </div> */}

        {transportation ? (
          <div className='mt-3 mb-2'>
            <span className='fw-semibold text-primary font-tiny'>{t('transportation')}</span>
            <div className='row mt-2'>
              <div className='col-auto text-lighter-gray pr-0'>{t('pickupLocation')}:</div>
              <div className='col fw-600 text-right'>
                {transportation.name} - {transportation.description}
              </div>
            </div>
            <div className='row mt-1'>
              <div className='col-auto text-lighter-gray pr-0'>{t('pickupTime')}:</div>
              <div className='col text-lighter-gray text-right'>
                {transportation.time}{' '}
                {parseInt(transportation?.time?.split(':')[0], 10) < 12 ? 'am' : 'pm'}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Summary;
